// The chicken of a floor drawn with three.js (behaviour in chicken.js). Seven meshes in one group —
// legs, body, head, wings and a contact shadow — on the shared geometries and materials
// (geometries.js, materials.js): a chicken owns no GPU resource, so removing it leaks nothing.
// It is never a pick target: the scene's picking ignores it and its meshes do not raycast.
import { Group, Mesh } from 'three';
import { CHICKEN_MODEL } from './geometries.js';
import { chickenArea, createChicken, stepChicken, chickenWakeAt, wantsFrames } from './chicken.js';

function noRaycast() {}

export class ChickenView {
  /**
   * @param {{geos:object, mats:object, layout:object, parent:import('three').Object3D, seed?:number, now?:number,
   *   at?:{x:number, z:number, yaw?:number}|null}} opts
   *   layout: the floor layout (layout.js floorLayout); now: wall clock (s); at: where it was before a rebuild
   */
  constructor({ geos, mats, layout, parent, seed = 1, now = 0, at = null }) {
    const M = CHICKEN_MODEL;
    this.parent = parent;
    this.state = createChicken({ area: chickenArea(layout), seed, now, at });
    this.group = new Group();
    this.group.name = 'chicken';
    this.group.scale.setScalar(M.scale);
    this.meshes = [];
    const part = (geo, mat, parentObj, x, y, z, cast = true) => {
      const m = new Mesh(geo, mat);
      m.position.set(x, y, z);
      m.castShadow = cast;
      m.receiveShadow = cast;
      m.raycast = noRaycast;
      parentObj.add(m);
      this.meshes.push(m);
      return m;
    };
    // Soft contact shadow, stays on the ground during a hop.
    this.blob = part(geos.plane, mats.contact, this.group, 0, 0.004, 0, false);
    this.blob.scale.set(0.62, 1, 0.82);
    this.legL = part(geos.chickenLeg, mats.chicken, this.group, M.hipX, M.hipY, 0);
    this.legR = part(geos.chickenLeg, mats.chicken, this.group, -M.hipX, M.hipY, 0);
    this.torso = part(geos.chickenBody, mats.chicken, this.group, 0, M.torsoY, 0);
    this.head = part(geos.chickenHead, mats.chicken, this.torso, 0, M.neckY - M.torsoY, M.neckZ);
    this.wingL = part(geos.chickenWing, mats.chicken, this.torso, M.wingX, M.wingY - M.torsoY, M.wingZ);
    this.wingR = part(geos.chickenWing, mats.chicken, this.torso, -M.wingX, M.wingY - M.torsoY, M.wingZ);
    parent.add(this.group);
    this.apply();
  }

  /**
   * Advances the behaviour and poses the meshes.
   * @param {number} dt seconds
   * @param {number} now wall clock (s)
   * @param {object} env chicken.js stepChicken env
   * @returns {boolean} true when the chicken needs the next frame
   */
  update(dt, now, env) {
    const before = this.state.mode === 'rest' && this.state.posed;
    const want = stepChicken(this.state, dt, now, env);
    // A still chicken has nothing new to pose (no work while it rests).
    if (!(before && !want)) this.apply();
    return want;
  }

  /** Wall clock (s) of the next wake-up of a resting chicken (chicken.js chickenWakeAt). */
  wakeAt(env) {
    return chickenWakeAt(this.state, env);
  }

  /** True while the chicken moves. */
  get moving() {
    return wantsFrames(this.state);
  }

  /** Writes the pose of the state into the meshes (no allocation). */
  apply() {
    const s = this.state;
    const M = CHICKEN_MODEL;
    const g = this.group;
    g.position.set(s.x, s.y, s.z);
    g.rotation.y = s.yaw;
    // The contact shadow stays on the ground and shrinks while the chicken is in the air.
    const lift = s.y / M.scale;
    this.blob.position.y = 0.004 - lift;
    const k = Math.max(0.4, 1 - lift * 2.2);
    this.blob.scale.set(0.62 * k, 1, 0.82 * k);
    this.legL.rotation.x = s.legL;
    this.legR.rotation.x = s.legR;
    this.torso.position.y = M.torsoY + s.bob - s.crouch;
    this.torso.rotation.x = s.bodyPitch;
    this.head.position.z = M.neckZ + s.headFwd;
    this.head.rotation.x = s.headPitch;
    this.wingL.rotation.z = s.wing;
    this.wingR.rotation.z = -s.wing;
  }

  /** Follows the floor's sun shadow setting (only the current floor casts). */
  setCastShadow(on) {
    for (const m of this.meshes) if (m !== this.blob) m.castShadow = on;
  }

  /** Removes the chicken (its geometries and materials are shared: not disposed here). */
  dispose() {
    this.parent.remove(this.group);
    this.group.clear();
    this.meshes = [];
  }
}
